import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { IPresTruncatedDataDTO } from 'API';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  AccordionArrowDisabled,
  AccordionArrowPurple,
  AccordionArrowPurpleClose,
  BigButton,
} from 'Images';
import { Fragment, useState, useEffect } from 'react';
import { PrescriptionsListItemMobile } from '../PrescriptionsListItemMobile';
import { PrescriptionsListItemDesktop } from '../PrescriptionsListItemDesktop';
import { sendEvent } from 'Utils/sendEvent';
import { CustomDivider } from 'Components';

interface IAccordionSection {
  title: string;
  subTitle: string;
  items: IPresTruncatedDataDTO[];
  defaultExpandedAccordion: boolean;
  titles: string[];
  onShowPrescriptionDetailsClick: (
    clickedPrescription: IPresTruncatedDataDTO
  ) => void;
  isDisabled?: boolean;
}

const initialItemsToDisplay = 8;

export const AccordionSection = ({
  title,
  subTitle,
  items,
  defaultExpandedAccordion,
  titles,
  onShowPrescriptionDetailsClick,
  isDisabled,
}: IAccordionSection) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );
  const [isExpanded, setIsExpanded] = useState(defaultExpandedAccordion);
  const [showAllItems, setShowAllItems] = useState(false);

  useEffect(() => {
    setIsExpanded(defaultExpandedAccordion);
  }, [defaultExpandedAccordion]);

  const toggleItemsDisplay = () => {
    isDesktop && sendEvent('Medicine', 'AllStatusOrder');
    setShowAllItems((prevState) => !prevState);
  };

  const renderListItem = (item: IPresTruncatedDataDTO, index: number) => {
    const ListItem = isDesktop
      ? PrescriptionsListItemDesktop
      : PrescriptionsListItemMobile;
    return (
      <ListItem
        key={index}
        titles={titles}
        item={item}
        onShowPrescriptionDetailsClick={onShowPrescriptionDetailsClick}
        itemRecordStatus={item.currentStatusLabel || ''}
      />
    );
  };

  const renderToggleButton = () => (
    <Stack
      onKeyUp={(e: React.KeyboardEvent) =>
        e.key === 'Enter' && toggleItemsDisplay()
      }
      tabIndex={0}
      onClick={toggleItemsDisplay}
      direction='row'
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        m: isDesktop ? '10px 50px' : undefined,
      }}
    >
      <Typography variant='body5' color='primary'>
        {showAllItems ? dictionary.seeLess : dictionary.toAllPrescriptions}
      </Typography>
      <Avatar
        src={BigButton}
        alt=''
        sx={{
          width: '46px',
          height: '50px',
          transform: `rotate(${showAllItems ? 90 : 270}deg)`,
        }}
      />
    </Stack>
  );

  return (
    <Accordion
      sx={{
        width: '100%',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'primary.shade_200',
        borderRadius: '16px !important',
        '&:before': { display: 'none' },
        mb: '24px !important',
        '.MuiAccordionSummary-root.Mui-disabled': {
          backgroundColor: 'primary.shade_50',
          opacity: 1,
          borderRadius: '16px',
        },
      }}
      expanded={isExpanded}
      disabled={isDisabled}
      onChange={() => setIsExpanded((prevState) => !prevState)}
    >
      <AccordionSummary
        expandIcon={
          <>
            {!isExpanded && (
              <Avatar
                src={isDisabled ? AccordionArrowDisabled : AccordionArrowPurple}
                sx={{
                  width: isDisabled ? '25px' : '32px',
                  height: isDisabled ? '25px' : '32px',
                  pr: isDisabled ? '10px' : undefined,
                }}
              />
            )}
            {isExpanded && (
              <Avatar
                src={AccordionArrowPurpleClose}
                sx={{ width: '32px', height: '32px', rotate: '180deg' }}
              />
            )}
          </>
        }
        sx={{ p: '8px 16px' }}
      >
        <>
          {isExpanded && (
            <CustomDivider
              style={{
                borderTop: '1px solid #E7EDFF',
              }}
            />
          )}
          {isDesktop ? (
            <Stack flexDirection='row' gap='5px'>
              <Typography>{title}</Typography>
              <span>-</span>
              <Typography variant='body2'>{subTitle}</Typography>
            </Stack>
          ) : (
            <Stack gap='5px' maxWidth={'252px'}>
              <Typography>{title}</Typography>
              <Typography variant='body6'>{subTitle}</Typography>
            </Stack>
          )}
        </>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container rowSpacing={0} columnSpacing={0} mb={'40px'}>
          <Grid
            component='ul'
            padding={0}
            container
            item
            xs={12}
            gap={2}
            justifyContent={isDesktop ? 'start' : 'center'}
            sx={{ width: '100%', mb: '40px', bgcolor: 'transparent' }}
          >
            {items &&
              items.map((item, index) => {
                if (showAllItems || index < initialItemsToDisplay) {
                  return renderListItem(item, index);
                }
                return null;
              })}
          </Grid>
        </Grid>
        {items && items.length > initialItemsToDisplay && renderToggleButton()}
      </AccordionDetails>
    </Accordion>
  );
};
